import { useState } from 'react'
import { podeFiscalizar } from '../../auth/permissoes'
import { useSessao } from '../../auth/contexto'
import { useAutoresDeRepositorio } from '../../api/queries/repositorios'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { TEXTOS } from '../../lib/glossario'
import type { AutorNaTela } from '../../types/repositorios'
import { DialogoDeEquipeInterna } from './DialogoDeEquipeInterna'
import { DialogoDeSetor } from './DialogoDeSetor'
import { LinhaDeAutor } from './LinhaDeAutor'
import { ordenarAutores } from './autores'

/**
 * Todas as pessoas que a coleta conhece, com o setor e a marca de equipe interna.
 *
 * É a única tela em que a equipe interna aparece por inteiro — o resto do módulo a exclui. Serve
 * para o fiscal conferir o casamento com o Diário e corrigir o que a regra errou.
 *
 * @returns a lista
 */
export function ListaDeAutores() {
  const t = TEXTOS.repositorios.autores
  const { usuario } = useSessao()
  const { data, isPending, isError, error } = useAutoresDeRepositorio()
  const [corrigindoSetor, setCorrigindoSetor] = useState<AutorNaTela | null>(null)
  const [mudandoEquipe, setMudandoEquipe] = useState<AutorNaTela | null>(null)
  const fiscal = podeFiscalizar(usuario)

  return (
    <section className="cartao overflow-x-auto p-5" aria-label={t.titulo}>
      <h2 className="flex items-center gap-1 text-base font-semibold text-slate-900">
        {t.titulo}
        <Explicacao termo="equipeInterna" />
      </h2>
      <p className="mt-1 max-w-prose text-sm text-slate-500">{t.ajuda}</p>
      {isPending && <Esqueleto linhas={3} className="mt-3" />}
      {isError && <AvisoDeErro erro={error} />}
      {data && data.length === 0 && <p className="mt-3 text-sm text-slate-600">{t.nenhuma}</p>}
      {data && data.length > 0 && (
        <table className="mt-3 min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500">
              <th className="px-3 py-2">{t.colunas.pessoa}</th>
              <th className="px-3 py-2">{t.colunas.setor}</th>
              <th className="px-3 py-2">{t.colunas.equipeInterna}</th>
              <th className="px-3 py-2 text-right">{t.colunas.commits}</th>
              <th className="px-3 py-2">
                <span className="sr-only">{TEXTOS.tabela.acoes}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {ordenarAutores(data).map((autor) => (
              <LinhaDeAutor
                key={autor.id}
                autor={autor}
                fiscal={fiscal}
                aoCorrigirSetor={() => setCorrigindoSetor(autor)}
                aoMudarEquipeInterna={() => setMudandoEquipe(autor)}
              />
            ))}
          </tbody>
        </table>
      )}
      {corrigindoSetor && (
        <DialogoDeSetor
          autor={{
            id: corrigindoSetor.id,
            nome: corrigindoSetor.nomeDeExibicao ?? corrigindoSetor.nome,
            email: corrigindoSetor.email,
            setor: corrigindoSetor.setor,
          }}
          aoFechar={() => setCorrigindoSetor(null)}
        />
      )}
      {mudandoEquipe && (
        <DialogoDeEquipeInterna
          autor={{
            id: mudandoEquipe.id,
            nome: mudandoEquipe.nomeDeExibicao ?? mudandoEquipe.nome,
            email: mudandoEquipe.email,
            equipeInterna: mudandoEquipe.equipeInterna,
          }}
          aoFechar={() => setMudandoEquipe(null)}
        />
      )}
    </section>
  )
}
